import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { toast } from "react-toastify";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import schema from "./schema";
import {
  createToolInOut,
  updateToolInOut,
} from "features/ToolInOuts/services/ToolInOutService";
import { ToolInOut } from "features/ToolInOuts/types/tool-in-out.types";
import {
  ToolInOutSourceType,
  ToolInOutSourceTypeLabel,
} from "features/ToolInOuts/constants/tool-in-out.constants";
import { getToolOptions } from "features/Tools/services/ToolService";
import { getWorkOrderOptions } from "features/WorkOrders/services/WorkOrderService";
import {
  WorkOrderOption,
  WorkOrderSingleValue,
} from "features/WorkOrders/components/WorkOrderSelectComponents";
import { getPettyWorkOptions } from "features/PettyWorks/services/PettyWorkService";
import {
  PettyWorkOption,
  PettyWorkSingleValue,
} from "features/PettyWorks/components/PettyWorkSelectComponents";
import { getAmcOptions } from "features/Amc/services/AmcService";
import {
  AmcOption,
  AmcSingleValue,
} from "features/Amc/components/AmcSelectComponents";
import { getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";
import {
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import { useProfile } from "features/Authentication/hooks/useProfile";

const SOURCE_TYPE_OPTIONS = [
  {
    value: ToolInOutSourceType.WORK_ORDER,
    label: ToolInOutSourceTypeLabel[ToolInOutSourceType.WORK_ORDER],
  },
  {
    value: ToolInOutSourceType.PETTY_WORK,
    label: ToolInOutSourceTypeLabel[ToolInOutSourceType.PETTY_WORK],
  },
  {
    value: ToolInOutSourceType.AMC,
    label: ToolInOutSourceTypeLabel[ToolInOutSourceType.AMC],
  },
  {
    value: ToolInOutSourceType.OTHER,
    label: ToolInOutSourceTypeLabel[ToolInOutSourceType.OTHER],
  },
];

function fetchOptionsForSourceType(sourceType: string) {
  if (sourceType === ToolInOutSourceType.WORK_ORDER) return getWorkOrderOptions;
  if (sourceType === ToolInOutSourceType.PETTY_WORK) return getPettyWorkOptions;
  return getAmcOptions;
}

function selectComponentsForSourceType(sourceType: string) {
  if (sourceType === ToolInOutSourceType.WORK_ORDER) {
    return { Option: WorkOrderOption, SingleValue: WorkOrderSingleValue };
  }
  if (sourceType === ToolInOutSourceType.PETTY_WORK) {
    return { Option: PettyWorkOption, SingleValue: PettyWorkSingleValue };
  }
  return { Option: AmcOption, SingleValue: AmcSingleValue };
}

function toYmd(d: Date): string {
  return moment(d).format("YYYY-MM-DD");
}

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * When null, create. When set, edit that row. The caller (list / deep-link
   * edit route) only opens this in edit mode when `dateIn == null`, or for
   * any row when the actor is Super Admin (post-return editing) — everyone
   * else is kept out before this form is ever reached.
   */
  editingRecord: ToolInOut | null;
};

export function ToolInOutCreateModal({
  show,
  onClose,
  onSuccess,
  editingRecord,
}: Props) {
  const isEdit = editingRecord != null;
  const { user } = useProfile();
  /** Any role can check a tool out against any user — default to self for convenience only. */
  const ownUserId = user?.id ?? null;

  const [userDefaultOption, setUserDefaultOption] = useState<
    { value: number; label: string } | undefined
  >(() => {
    if (editingRecord) {
      return {
        value: editingRecord.user.id,
        label:
          [editingRecord.user.firstName, editingRecord.user.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() || `User #${editingRecord.user.id}`,
      };
    }
    if (user?.id) {
      return {
        value: user.id,
        label:
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          "You",
      };
    }
    return undefined;
  });

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      toolId: editingRecord?.toolId ?? "",
      sourceType: editingRecord?.sourceType ?? ToolInOutSourceType.WORK_ORDER,
      sourceId: editingRecord?.sourceId ?? "",
      otherDescription: editingRecord?.otherDescription ?? "",
      referenceNo: editingRecord?.referenceNo ?? "",
      userId: editingRecord?.userId ?? (ownUserId ?? ""),
      quantity: editingRecord?.quantity ?? 1,
      dateOut: editingRecord?.dateOut ? editingRecord.dateOut.slice(0, 10) : "",
    },
    validationSchema: schema,
    onSubmit: async (values: any) => {
      const quantity = Number(values.quantity);
      const referenceNo = values.referenceNo?.trim()
        ? values.referenceNo.trim()
        : null;
      const userId = Number(values.userId);

      try {
        let response;
        if (isEdit && editingRecord) {
          response = await updateToolInOut(editingRecord.id, {
            toolId: Number(values.toolId),
            userId,
            quantity,
            dateOut: values.dateOut || null,
            referenceNo,
          });
        } else {
          const sourceType = values.sourceType as ToolInOutSourceType;
          const isOther = sourceType === ToolInOutSourceType.OTHER;
          response = await createToolInOut({
            toolId: Number(values.toolId),
            userId,
            sourceType,
            sourceId: isOther ? null : Number(values.sourceId),
            otherDescription: isOther
              ? String(values.otherDescription).trim()
              : null,
            referenceNo,
            quantity,
            dateOut: values.dateOut || null,
          });
        }
        if (response.success) {
          toast.success(response.message);
          onSuccess();
          formik.resetForm();
          onClose();
        }
      } catch (err: any) {
        // Server 500s (treated as a conflict) when the row is already
        // returned — just surface whatever message it sends back.
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update record" : "Failed to create record"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSourceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.setFieldValue("sourceType", value);
    formik.setFieldValue("sourceId", "");
  };

  const selectedSourceType = formik.values.sourceType as ToolInOutSourceType;

  return (
    <Modal isOpen={show} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit tool in/out" : "Check out a tool"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <Row className="g-3">
            <Col md={6}>
              <Label className="form-label">
                Tool <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getToolOptions}
                placeholder="Search tools…"
                value={formik.values.toolId}
                onChange={(opt: any) =>
                  formik.setFieldValue("toolId", opt?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("toolId", true)}
                isInvalid={formik.touched.toolId && !!formik.errors.toolId}
              />
              {formik.touched.toolId && formik.errors.toolId ? (
                <FormFeedback type="invalid" className="d-block">
                  {String(formik.errors.toolId)}
                </FormFeedback>
              ) : null}
            </Col>

            <Col md={6}>
              <Label className="form-label">
                Concerned person <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getUserOptionsWithEmployeeProfile}
                components={{
                  Option: EmployeeOption,
                  SingleValue: EmployeeSingleValue,
                }}
                placeholder="Search users…"
                defaultOption={userDefaultOption}
                value={formik.values.userId}
                onChange={(opt: any) => {
                  const userId =
                    opt && !Array.isArray(opt) && opt.value != null
                      ? Number(opt.value)
                      : undefined;
                  formik.setFieldValue("userId", userId ?? "");
                  setUserDefaultOption(
                    opt && !Array.isArray(opt) && userId != null
                      ? { value: userId, label: opt.label }
                      : undefined,
                  );
                }}
                onBlur={() => formik.setFieldTouched("userId", true)}
                isInvalid={formik.touched.userId && !!formik.errors.userId}
              />
              {formik.touched.userId && formik.errors.userId ? (
                <FormFeedback type="invalid" className="d-block">
                  {String(formik.errors.userId)}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>

          <hr className="my-4 text-muted opacity-25" />
          <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
            Linked to
          </div>
          <Row className="g-3">
            <Col md={12}>
              <Label className="form-label">Source type</Label>
              <div className="d-flex gap-4 pt-1 flex-wrap">
                {SOURCE_TYPE_OPTIONS.map((opt) => (
                  <div className="form-check" key={opt.value}>
                    <Input
                      type="radio"
                      className="form-check-input"
                      id={`tool-in-out-source-type-${opt.value}`}
                      name="sourceType"
                      value={opt.value}
                      checked={formik.values.sourceType === opt.value}
                      onChange={handleSourceTypeChange}
                      onBlur={formik.handleBlur}
                      disabled={isEdit}
                    />
                    <Label
                      className="form-check-label"
                      for={`tool-in-out-source-type-${opt.value}`}
                    >
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </div>
              {formik.errors.sourceType &&
                (formik.touched.sourceType || formik.submitCount > 0) && (
                  <FormFeedback type="invalid" className="d-block">
                    {String(formik.errors.sourceType)}
                  </FormFeedback>
                )}
            </Col>

            <Col md={12} className="mt-2">
              {isEdit ? (
                <>
                  <Label className="form-label">
                    {ToolInOutSourceTypeLabel[
                      editingRecord?.sourceType as ToolInOutSourceType
                    ] ?? "Source"}
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    value={editingRecord?.workLabel ?? "—"}
                    readOnly
                    disabled
                  />
                  <small className="text-muted d-block mt-1">
                    The link can't be changed after checkout — delete and
                    re-create this row against a different source if needed.
                  </small>
                </>
              ) : selectedSourceType === ToolInOutSourceType.OTHER ? (
                <>
                  <Label
                    className="form-label"
                    for="tool-in-out-other-description"
                  >
                    Description <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="tool-in-out-other-description"
                    type="text"
                    name="otherDescription"
                    placeholder="Describe where this tool is going…"
                    value={formik.values.otherDescription}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(
                        formik.touched.otherDescription &&
                        formik.errors.otherDescription
                      )
                    }
                  />
                  {formik.touched.otherDescription &&
                  formik.errors.otherDescription ? (
                    <FormFeedback type="invalid">
                      {String(formik.errors.otherDescription)}
                    </FormFeedback>
                  ) : null}
                </>
              ) : (
                <>
                  <Label className="form-label">
                    {ToolInOutSourceTypeLabel[selectedSourceType] ?? "Source"}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <AsyncPaginatedSelect
                    key={selectedSourceType}
                    fetchOptions={fetchOptionsForSourceType(selectedSourceType)}
                    components={selectComponentsForSourceType(
                      selectedSourceType,
                    )}
                    placeholder="Search…"
                    value={formik.values.sourceId}
                    onChange={(val: any) =>
                      formik.setFieldValue("sourceId", val?.value ?? "")
                    }
                    onBlur={() => formik.setFieldTouched("sourceId", true)}
                    isInvalid={
                      formik.touched.sourceId && !!formik.errors.sourceId
                    }
                  />
                  {formik.touched.sourceId && formik.errors.sourceId ? (
                    <FormFeedback type="invalid" className="d-block">
                      {String(formik.errors.sourceId)}
                    </FormFeedback>
                  ) : null}
                </>
              )}
            </Col>
          </Row>

          <hr className="my-4 text-muted opacity-25" />

          <Row className="g-3">
            <Col md={4}>
              <Label className="form-label" for="tool-in-out-quantity">
                Quantity <span className="text-danger">*</span>
              </Label>
              <Input
                id="tool-in-out-quantity"
                type="number"
                min={1}
                step="1"
                name="quantity"
                value={formik.values.quantity}
                onChange={(e) =>
                  formik.setFieldValue(
                    "quantity",
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.quantity && formik.errors.quantity)}
              />
              {formik.touched.quantity && formik.errors.quantity ? (
                <FormFeedback type="invalid">
                  {String(formik.errors.quantity)}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md={4}>
              <Label className="form-label" for="tool-in-out-date-out">
                Date out{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Flatpickr
                id="tool-in-out-date-out"
                className="form-control"
                placeholder="Defaults to now"
                options={{
                  altInput: true,
                  altFormat: "d M, Y",
                  dateFormat: "Y-m-d",
                }}
                value={
                  formik.values.dateOut
                    ? new Date(`${formik.values.dateOut}T12:00:00`)
                    : ""
                }
                onChange={(dates: Date[]) =>
                  formik.setFieldValue(
                    "dateOut",
                    dates[0] ? toYmd(dates[0]) : "",
                  )
                }
              />
            </Col>
            <Col md={4}>
              <Label className="form-label" for="tool-in-out-reference-no">
                Reference no.{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Input
                id="tool-in-out-reference-no"
                type="text"
                name="referenceNo"
                placeholder="Free entry — not system generated"
                value={formik.values.referenceNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Save changes"
                  : "Check out"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
